import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import { TriangleAlert } from 'lucide-react';
import Select from '../../components/Select';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { SELECT_STYLES } from '../../utils/const';
import * as Route from '../../utils/serverRoutes';
import TeamMembers from './TeamMembers';
import { transformResponseData } from '../../utils/helpers';
import makeRequest from '../../utils/makeRequest';

export const inputOptions = (collection, users) =>
  collection
    .filter((item) =>
      users.find((user) => user.email !== item.attributes.email),
    )
    .map((item) => {
      return {
        label: item.attributes.email,
        value: transformResponseData(item),
      };
    });

export default function CreateBmcModal({
  visible,
  onClose,
  locales,
  locales: { teams, common },
}) {
  // teams.createProject = undefined;
  const defaultSelectValue = { label: '', value: '' };
  const [projectName, setProjectName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState(defaultSelectValue);
  const [options, setOptions] = useState([]);
  const [users, setUsers] = useState([]);

  const {
    bmc: { addBmc },
  } = useDispatch();

  const dispatch = useDispatch();

  const {
    user: { current },
    bmc: {
      current: { id: currentBmcId },
    },
  } = useSelector((state) => state);

  const performSearchRef = useRef();
  performSearchRef.current = (value) => {
    if (value) {
      makeRequest({
        dispatch,
        url: Route.searchSuitableStudent(value, currentBmcId),
        success: (response) => {
          setOptions(inputOptions(response.data.data, [...users, current]));
        },
      });
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        performSearchRef.current(value);
      }, 700),
    [],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const searchBmcUsers = (value) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  const removeBmcUser = (id) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(common.areYouSure)) {
      setUsers(users.filter((e) => e.id !== id));
    }
  };

  const changeSelect = ({ value }) => {
    setInputValue('');
    setSelectValue(defaultSelectValue);
    setUsers(_.uniqBy([...users, value], 'id'));
  };

  const createProject = async () => {
    const newProject = await addBmc({
      name: projectName,
      userEmails: users.map((e) => e.email),
    });

    if (newProject) {
      onClose();
      window.location.href = `/bmcs/${newProject.id}`;
    }
  };

  return (
    <Modal
      className="bmc-modal--form"
      isOpen={visible}
      onClose={onClose}
      title={teams.createYourNewProject}
    >
      <div className="bmc-form">
        <div className="flex flex-row gap-2 items-center justify-between w-full">
          <div className="flex flex-col w-1/2">
            <p className="bmc-form-label">{teams.className}</p>
            <p className="bmc-form-label-sub">
              {current?.attributes?.parentClassrooms &&
              current?.attributes?.parentClassrooms.length > 0
                ? current?.attributes?.parentClassrooms[0].name
                : 'N/A'}
            </p>
          </div>
          <div className="flex flex-col w-1/2">
            <p className="bmc-form-label">{teams.classId}</p>
            <p className="bmc-form-label-sub">
              {current?.attributes?.parentClassrooms &&
              current?.attributes?.parentClassrooms.length > 0
                ? current?.attributes?.parentClassrooms[0].id
                : 'N/A'}
            </p>
          </div>
        </div>

        <div className="bmc-form-fieldset margin-top-20">
          <label htmlFor="project-name" className="bmc-form-label">
            <span>*</span> {teams.projectName}
          </label>
          <input
            id="project-name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="bmc-form-input"
            placeholder={teams.projectNamePlaceholder}
            data-hj-allow
          />
        </div>

        <div className="bmc-form-fieldset margin-top-20 margin-bottom-20">
          <label htmlFor="email" className="bmc-form-label">
            {teams.orInviteTeammatesViaEmail}
          </label>
          <p className="bmc-form-label-sub">
            {teams.orInviteTeammatesViaEmailSub}
          </p>

          <p className="bmc-form-label-sub text-red-500 font-bold mt-2 items-center gap-2 flex flex-row">
            <TriangleAlert className="w-4 h-4" />
            {teams.important}
          </p>
          <p className="bmc-form-label-sub">
            {teams.orInviteTeammatesViaEmailNote}
          </p>
          <Select
            id="email"
            className="bmc-form-select"
            styles={SELECT_STYLES}
            options={options}
            inputValue={inputValue}
            value={selectValue}
            placeholder={teams.orInviteTeammatesViaEmailPlaceholder}
            onInputChange={searchBmcUsers}
            onChange={changeSelect}
            noOptionsMessage={() => teams.noOptionsMessage}
          />
        </div>

        <TeamMembers
          users={users}
          removeBmcUser={removeBmcUser}
          locales={locales}
        />

        <Button
          className="flex flex-1 w-full"
          disabled={!projectName}
          onClick={createProject}
        >
          {teams.createProject}
        </Button>
      </div>
    </Modal>
  );
}
